import { useState } from 'react'
import { SCHOOL_TYPE_OPTIONS, type SchoolType } from '@common/utils/school-type'
import { PREFECTURE_OPTIONS } from '@common/utils/prefecture'
import { Form } from 'react-bootstrap'
import { useSystemConfig } from '@/context/SystemConfigContext'
import { PRINCIPAL_NAME_MAX_LENGTH, SCHOOL_NAME_MAX_LENGTH } from '@common/utils/name-length'

interface RegisterData {
  schoolType: SchoolType
  schoolName: string
  postalCode: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description: string
  contactName: string
  contactEmail: string
}

interface FieldErrors {
  schoolType?: string | null
  schoolName?: string | null
  postalCode?: string | null
  prefecture?: string | null
  city?: string | null
  address?: string | null
  phone?: string | null
  principal?: string | null
  contactName?: string | null
  contactEmail?: string | null
}

interface RegisterFormProps {
  registerData: RegisterData
  error: string
  fieldErrors: FieldErrors
  isPostalCodeSearching: boolean
  onRegisterChange: (field: keyof RegisterData, value: string) => void
  onPostalCodeSearch: () => void
  onClearFieldError: (field: keyof FieldErrors) => void
  onSubmit: (e: React.FormEvent) => void
}

function RegisterForm({
  registerData,
  error,
  fieldErrors,
  isPostalCodeSearching,
  onRegisterChange,
  onPostalCodeSearch,
  onClearFieldError,
  onSubmit,
}: RegisterFormProps) {
  const { termsOfServiceUrl } = useSystemConfig()
  const [hasOpenedTerms, setHasOpenedTerms] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const termsRequired = Boolean(termsOfServiceUrl)
  const canSubmitTerms = termsRequired && hasOpenedTerms && agreedToTerms
  const hasPrefectureOption = PREFECTURE_OPTIONS.some((opt) => opt.value === registerData.prefecture)

  const handleFormSubmit = (e: React.FormEvent) => {
    if (!canSubmitTerms) {
      e.preventDefault()
      return
    }
    onSubmit(e)
  }

  return (
    <form onSubmit={handleFormSubmit} className="register-form">
      {/* 情報メッセージ */}
      <div className="info-message">
        <p>
        仮登録後、サポートチームよりお電話で登録内容を確認させていただきます。
        確認後、本登録となりアカウントが開設されます。
        </p>
      </div>

      {/* ログインIDセクション */}
      <div className="form-section">
        <h3 className="form-section-title">ログインID</h3>
        <div className={`form-group ${fieldErrors.contactEmail ? 'has-error' : ''}`}>
          <label htmlFor="register-contact-email">
            ログインID（メールアドレス）
            <span className="required-badge">必須</span>
          </label>
          <input
            id="register-contact-email"
            type="email"
            value={registerData.contactEmail}
            onChange={(e) => {
              onRegisterChange('contactEmail', e.target.value)
              if (fieldErrors.contactEmail) onClearFieldError('contactEmail')
            }}
            placeholder="example@irogami.com"
            autoComplete="email"
            className={fieldErrors.contactEmail ? 'error' : ''}
          />
          {fieldErrors.contactEmail && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.contactEmail}</span>
            </div>
          )}
        </div>
        <p className="form-caution">
          ※学校代表メールアドレスでの登録を推奨
        </p>
      </div>

      {/* 学校情報セクション */}
      <div className="form-section">
        <h3 className="form-section-title">学校情報</h3>
        <Form.Group className={`mb-3 ${fieldErrors.schoolType ? 'has-error' : ''}`}>
          <Form.Label className="form-label">
            学校区分
            <span className="required-badge">必須</span>
          </Form.Label>
          <div className="school-type-toggle-group" role="radiogroup" aria-label="学校区分">
            {SCHOOL_TYPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`school-type-btn ${registerData.schoolType === opt.value ? 'active' : ''}`}
              >
                <input
                  type="radio"
                  name="register-school-type"
                  value={opt.value}
                  checked={registerData.schoolType === opt.value}
                  onChange={() => {
                    onRegisterChange('schoolType', opt.value)
                    if (fieldErrors.schoolType) onClearFieldError('schoolType')
                  }}
                  aria-checked={registerData.schoolType === opt.value}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {fieldErrors.schoolType && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.schoolType}</span>
            </div>
          )}
        </Form.Group>
        <div className={`form-group ${fieldErrors.schoolName ? 'has-error' : ''}`}>
          <label htmlFor="register-school-name">
            学校名
            <span className="required-badge">必須</span>
          </label>
          <div className="register-form-input-wrap">
            <input
              id="register-school-name"
              type="text"
              value={registerData.schoolName}
              maxLength={SCHOOL_NAME_MAX_LENGTH}
              onChange={(e) => {
                onRegisterChange('schoolName', e.target.value)
                if (fieldErrors.schoolName) onClearFieldError('schoolName')
              }}
              placeholder="例：東京都立第一中学校"
              className={fieldErrors.schoolName ? 'error' : ''}
            />
            <span className="register-form-char-count" aria-live="polite">
              {registerData.schoolName.length}/{SCHOOL_NAME_MAX_LENGTH}
            </span>
          </div>
          {fieldErrors.schoolName && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.schoolName}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.postalCode ? 'has-error' : ''}`}>
          <label htmlFor="register-postal-code">
            郵便番号
            <span className="required-badge">必須</span>
          </label>
          <div className="register-postal-code-row">
            <input
              id="register-postal-code"
              type="text"
              value={registerData.postalCode}
              onChange={(e) => {
                onRegisterChange('postalCode', e.target.value)
                if (fieldErrors.postalCode) onClearFieldError('postalCode')
              }}
              placeholder="1234567"
              className={fieldErrors.postalCode ? 'error' : ''}
            />
            <button
              type="button"
              className="postal-code-search-button"
              onClick={onPostalCodeSearch}
              disabled={isPostalCodeSearching}
            >
              {isPostalCodeSearching ? '検索中...' : '検索'}
            </button>
          </div>
          <p className="register-postal-code-hint">「-」なしで7桁で入力</p>
          {fieldErrors.postalCode && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.postalCode}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.prefecture ? 'has-error' : ''}`}>
          <label htmlFor="register-prefecture">
            都道府県
            <span className="required-badge">必須</span>
          </label>
          <select
            id="register-prefecture"
            value={registerData.prefecture}
            onChange={(e) => {
              onRegisterChange('prefecture', e.target.value)
              if (fieldErrors.prefecture) onClearFieldError('prefecture')
            }}
            className={fieldErrors.prefecture ? 'error' : ''}
          >
            <option value="">都道府県を選択</option>
            {!hasPrefectureOption && registerData.prefecture && (
              <option value={registerData.prefecture}>{registerData.prefecture}</option>
            )}
            {PREFECTURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {fieldErrors.prefecture && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.prefecture}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.city ? 'has-error' : ''}`}>
          <label htmlFor="register-city">
            市区町村
            <span className="required-badge">必須</span>
          </label>
          <input
            id="register-city"
            type="text"
            value={registerData.city}
            onChange={(e) => {
              onRegisterChange('city', e.target.value)
              if (fieldErrors.city) onClearFieldError('city')
            }}
            placeholder="渋谷区"
            className={fieldErrors.city ? 'error' : ''}
          />
          {fieldErrors.city && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.city}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.address ? 'has-error' : ''}`}>
          <label htmlFor="register-address">
            それ以降の住所
            <span className="required-badge">必須</span>
          </label>
          <input
            id="register-address"
            type="text"
            value={registerData.address}
            onChange={(e) => {
              onRegisterChange('address', e.target.value)
              if (fieldErrors.address) onClearFieldError('address')
            }}
            placeholder="神南1-1-1"
            className={fieldErrors.address ? 'error' : ''}
          />
          {fieldErrors.address && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.address}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.phone ? 'has-error' : ''}`}>
          <label htmlFor="register-phone">
            電話番号
            <span className="required-badge">必須</span>
          </label>
          <input
            id="register-phone"
            type="tel"
            value={registerData.phone}
            onChange={(e) => {
              onRegisterChange('phone', e.target.value)
              if (fieldErrors.phone) onClearFieldError('phone')
            }}
            placeholder="0312345678"
            className={fieldErrors.phone ? 'error' : ''}
          />
          {fieldErrors.phone && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.phone}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.principal ? 'has-error' : ''}`}>
          <label htmlFor="register-principal">
            校長名
            <span className="required-badge">必須</span>
          </label>
          <div className="register-form-input-wrap">
            <input
              id="register-principal"
              type="text"
              value={registerData.principal}
              maxLength={PRINCIPAL_NAME_MAX_LENGTH}
              onChange={(e) => {
                onRegisterChange('principal', e.target.value)
                if (fieldErrors.principal) onClearFieldError('principal')
              }}
              placeholder="山田 太郎"
              className={fieldErrors.principal ? 'error' : ''}
            />
            <span className="register-form-char-count" aria-live="polite">
              {registerData.principal.length}/{PRINCIPAL_NAME_MAX_LENGTH}
            </span>
          </div>
          {fieldErrors.principal && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.principal}</span>
            </div>
          )}
        </div>
        <div className={`form-group ${fieldErrors.contactName ? 'has-error' : ''}`}>
          <label htmlFor="register-contact-name">
            申し込みご担当者名
            <span className="required-badge">必須</span>
          </label>
          <input
            id="register-contact-name"
            type="text"
            value={registerData.contactName}
            onChange={(e) => {
              onRegisterChange('contactName', e.target.value)
              if (fieldErrors.contactName) onClearFieldError('contactName')
            }}
            placeholder="田中 花子"
            className={fieldErrors.contactName ? 'error' : ''}
          />
          {fieldErrors.contactName && (
            <div className="field-error">
              <i className="bi bi-exclamation-circle"></i>
              <span>{fieldErrors.contactName}</span>
            </div>
          )}
        </div>
      </div>

      {/* 備考セクション */}
      <div className="form-section">
        <div className="form-group">
          <label htmlFor="register-description">備考</label>
          <textarea
            id="register-description"
            value={registerData.description}
            onChange={(e) => onRegisterChange('description', e.target.value)}
            placeholder="事前にご連絡事項がございましたら、ご入力ください。"
            rows={4}
            className="form-textarea"
          />
        </div>
        {termsOfServiceUrl ? (
          <div className="register-terms-agree" style={{ marginTop: '0.75rem' }}>
            <div
              className="register-terms-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'flex-start',
              }}
            >
              <input
                id="register-terms-agree"
                type="checkbox"
                checked={agreedToTerms}
                disabled={!hasOpenedTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                aria-describedby="register-terms-hint"
                style={{
                  width: '16px',
                  height: '16px',
                  minWidth: '16px',
                  margin: 0,
                  padding: 0,
                  appearance: 'auto',
                }}
              />
              <div
                id="register-terms-label"
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--irogami-text-main, #3d4f5c)',
                  lineHeight: 1.4,
                }}
              >
                <a
                  href={termsOfServiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="register-terms-link-button"
                  onClick={() => setHasOpenedTerms(true)}
                >
                  利用規約およびプライバシーポリシー
                </a>
                に同意する
              </div>
            </div>
            {!hasOpenedTerms && (
              <small id="register-terms-hint" className="text-muted d-block mt-1">
                ※内容を確認後、チェックが可能になります。
              </small>
            )}
          </div>
        ) : (
          <div className="register-terms-unavailable" style={{ marginTop: '0.75rem' }}>
            <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
              現在、利用規約の設定のため仮登録申請を受け付けておりません。
            </p>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      <button type="submit" className="login-button" disabled={!canSubmitTerms}>
        仮登録申請
      </button>
    </form>
  )
}

export default RegisterForm

